import React, { useEffect, useState } from 'react';
import { Col, Row, Form, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import CallList from 'pages/hvaqualityassurance/CallList';
import {
  fetchHVACampaignNames,
  fetchHVAServiceNames,
  HVACampaignName,
  HVAServiceName
} from 'services/hvaservicecampaign.service';
import { getAllHVAAgents, HVAAgent } from 'services/hvaagent.service';
import CallQAControl from 'pages/hvaqualityassurance/CallQAControl';

const CallAnalysis = () => {
  const [campaigns, setCampaigns] = useState<HVACampaignName[]>([]);
  const [services, setServices] = useState<HVAServiceName[]>([]);
  const [hvaagents, setHVAAgents] = useState<HVAAgent[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [formData, setFormData] = useState({
    service: '',
    campaign: '',
    hva: '',
    firstName: '',
    lastName: '',
    accountID: '',
    customerID: '',
    phoneNumber: ''
  });
  const [submittedData, setSubmittedData] = useState({
    service: '',
    campaign: '',
    hva: '',
    firstName: '',
    lastName: '',
    accountID: '',
    customerID: '',
    phoneNumber: '',
    startDate: '',
    endDate: ''
  });
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  // eslint-disable-next-line
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  // eslint-disable-next-line
  const [selectedDateYPhone, setSelectedDateYPhone] = useState<string | null>(null);
  const [selectedFilename, setselectedFilename] = useState<string | null>(null);

  // Función de callback que se pasará a CallList
  // eslint-disable-next-line
  const handleCallIdSelect = (callId: string, campaignId: number, customer: string, dateYphone: string, audiosource: string) => {
    setSelectedCallId(callId);
    setSelectedCampaignId(campaignId);
    setSelectedCustomer(customer);
    setSelectedDateYPhone(dateYphone);
    setselectedFilename(audiosource);
  };

  // Estado para controlar la visibilidad del formulario
  const [formVisible, setFormVisible] = useState(true);

  // Para forzar el renderizado del componente hijo con una nueva clave
  const [renderKey, setRenderKey] = useState(0);

  // Para manejar las fechas sin hora
  const formatDateWithoutTime = (date: Date): string => {
    const offset = date.getTimezoneOffset() * 60000; // Offset en milisegundos
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignNames = await fetchHVACampaignNames();
        setCampaigns(campaignNames);
      } catch (error) {
        alert('Error loading campaigns:');
      }
    };

    loadCampaigns();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const serviceNames = await fetchHVAServiceNames();
        setServices(serviceNames);
      } catch (error) {
        alert('Error loading services:');
      }
    };

    loadServices();
  }, []);

  useEffect(() => {
    const loadHVAAgents = async () => {
      try {
        const hvaAgents = await getAllHVAAgents();
        setHVAAgents(hvaAgents);
      } catch (error) {
        alert('Error loading services:');
      }
    };

    loadHVAAgents();
  }, []);

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { id: string; value: string } }
  ) => {
    const { id, value } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleDateChange = (id: string, date: Date[]) => {
    const selectedDate = date.length > 0 ? date[0] : null;
    if (id === 'startDate') {
      setStartDate(selectedDate);
    } else if (id === 'endDate') {
      setEndDate(selectedDate);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setSubmittedData({
      ...formData,
      startDate: formatDateWithoutTime(startDate),
      endDate: formatDateWithoutTime(endDate)
    });

    // Cambiar la clave para forzar el renderizado del componente hijo
    setRenderKey(prevKey => prevKey + 1);

    // Ocultar el formulario después de la consulta
    setFormVisible(false);
  };

  const toggleFormVisibility = () => {
    setFormVisible(!formVisible);
  };

  return (
    <>
      <Row className="gy-3 mb-3 justify-content-between align-items-center">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Call Quality Control</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Start by finding the calls you want to analyze
          </h5>
        </Col>
        <Col xs="auto" className="h-100 pt-5">
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            onClick={toggleFormVisibility}
          >
            <FontAwesomeIcon
              icon={formVisible ? faChevronUp : faChevronDown}
              className="me-2"
            />
            {formVisible ? 'Hide Search Form' : 'Show Search Form'}
          </Button>
        </Col>
      </Row>

      <Collapse in={formVisible}>
        <div>
          <Row as="form" className="g-2 mb-4" onSubmit={handleSubmit}>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Service</label>
              <Form.Select
                id="service"
                value={formData.service}
                onChange={handleInputChange}
              >
                <option></option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Campaign</label>
              <Form.Select
                id="campaign"
                value={formData.campaign}
                onChange={handleInputChange}
              >
                <option></option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">HVA</label>
              <Form.Select
                id="hva"
                value={formData.hva}
                onChange={handleInputChange}
              >
                <option></option>
                {hvaagents.map(hva => (
                  <option key={hva.id} value={hva.id}>
                    {hva.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Start Date</label>
              <DatePicker
                value={startDate ? [startDate] : []}
                onChange={date => handleDateChange('startDate', date)}
                options={{ dateFormat: 'M j, Y' }}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">End Date</label>
              <DatePicker
                value={endDate ? [endDate] : []}
                onChange={date => handleDateChange('endDate', date)}
                options={{ dateFormat: 'M j, Y' }}
              />
            </Col>
            <Col></Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">First name</label>
              <Form.Control
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Last name</label>
              <Form.Control
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Account ID</label>
              <Form.Control
                type="text"
                id="accountID"
                value={formData.accountID}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Customer ID</label>
              <Form.Control
                type="text"
                id="customerID"
                value={formData.customerID}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label">Phone number</label>
              <Form.Control
                type="text"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
            </Col>
            <Col xs={4} lg={4} xl={2} className="px-1">
              <label className="form-label"> </label>
              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  className="w-100"
                  type="submit"
                  style={{ height: '36px' }}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-2 fs-8" />
                  Find calls
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Collapse>

      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-0 mt-0 mb-0 border-top border-bottom">
        <Row className="gy-3 mt-1 justify-content-between bg-body-emphasis">
          <CallList
            key={renderKey} // Clave dinámica para forzar el renderizado
            service={submittedData.service}
            campaign={submittedData.campaign}
            hva={submittedData.hva}
            firstName={submittedData.firstName}
            lastName={submittedData.lastName}
            accountID={submittedData.accountID}
            customerID={submittedData.customerID}
            phoneNumber={submittedData.phoneNumber}
            startDate={submittedData.startDate}
            endDate={submittedData.endDate}
            selectedCallId={selectedCallId}
            onCallIdSelect={handleCallIdSelect}
          />
        </Row>
      </div>
      {selectedCallId && (
        <div className="mx-n4 px-n4 mx-lg-n6 px-lg-n6 bg-body pt-0 mt-0">
          <CallQAControl
            selectedcallid={selectedCallId}
            selectedcampaignid={selectedCampaignId || 0}
            selectedcustomer={selectedCustomer || ''}
            selecteddateyphone={selectedDateYPhone || ''}
            selectedfilename={selectedFilename || ''}
          />
        </div>
      )}
    </>
  );
};

export default CallAnalysis;
